import React, { useRef, useState, useEffect } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import { AuthProvider } from './contexts/AuthContext';
import { Calendar, CalendarRef } from './components/Calendar';
import { Header } from './components/Header';
import './App.css';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

function App() {
  const calendarRef = useRef<CalendarRef>(null);
  const [calendarState, setCalendarState] = useState({
    azureConnected: false,
    azureStatus: 'checking',
    isRefreshing: false
  });

  // Update calendar state periodically to keep header in sync
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const updateCalendarState = () => {
      if (calendarRef.current) {
        const newState = {
          azureConnected: calendarRef.current.azureConnected,
          azureStatus: calendarRef.current.azureStatus,
          isRefreshing: calendarRef.current.isRefreshing          
        };

        setCalendarState(newState);

        // Clear interval once connected and not syncing/refreshing
        if (newState.azureConnected && !newState.isRefreshing && interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };

    // Update immediately
    updateCalendarState();

    // Only start polling if not already connected or still syncing/refreshing
    if (calendarRef.current && (!calendarRef.current.azureConnected || calendarRef.current.isRefreshing)) {
      interval = setInterval(updateCalendarState, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  // Restart polling when sync operations begin
  useEffect(() => {
    if ((calendarState.isRefreshing) && calendarState.azureConnected) {
      const interval = setInterval(() => {
        if (calendarRef.current) {
          const newState = {
            azureConnected: calendarRef.current.azureConnected,
            azureStatus: calendarRef.current.azureStatus,
            isRefreshing: calendarRef.current.isRefreshing
          };
          setCalendarState(newState);

          // Clear interval once operations complete
          if (!newState.isRefreshing) {
            clearInterval(interval);
          }
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [calendarState.isRefreshing, calendarState.azureConnected]);

  const handleRefreshFromAzure = () => {
    calendarRef.current?.handleRefreshFromAzure();
  };

  const handleAddEvent = () => {
    calendarRef.current?.handleAddEvent();
  };

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider msalInstance={msalInstance}>
        <div className="App">
          <Header
            azureConnected={calendarState.azureConnected}
            azureStatus={calendarState.azureStatus}
            isRefreshing={calendarState.isRefreshing}
            onRefreshFromAzure={handleRefreshFromAzure}
            onAddEvent={handleAddEvent}
          />
          <main className="main-content">
            <Calendar ref={calendarRef} />
          </main>
        </div>
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;